import { useState, useCallback, useEffect } from 'react';
import { useChatContext } from 'stream-chat-react';
import type { LocalMessage, Event } from 'stream-chat';
import { formatMessage } from 'stream-chat';
import { useActiveChannelState } from '@features/components/Chat/components/ChatContent/Messages/hooks/useActiveChannelState';
import { STREAM_MESSAGE_FIELD_PINNED, StreamEventType } from '../../constants';

export const useChatMessagesState = () => {
  const { channel } = useChatContext();

  const [pinnedMessages, setPinnedMessages] = useState<LocalMessage[]>([]);
  const [isPinnedPanelOpen, setIsPinnedPanelOpen] = useState(false);
  const [loadingPinnedMessages, setLoadingPinnedMessages] = useState(false);
  const [isDetailsPanelOpen, setIsDetailsPanelOpen] = useState(false);
  const { isPinned: isChannelPinned, isMuted } = useActiveChannelState();

  const fetchPinnedMessages = useCallback(async () => {
    if (!channel) return;
    try {
      setLoadingPinnedMessages(true);
      const response = await channel.getPinnedMessages({ limit: 100 });
      setPinnedMessages(
        response.messages
          .sort((a, b) => new Date(a.pinned_at || 0).getTime() - new Date(b.pinned_at || 0).getTime())
          .map((m) => formatMessage(m)),
      );
    } finally {
      setLoadingPinnedMessages(false);
    }
  }, [channel]);

  const toggleMuteChannel = useCallback(async () => {
    if (!channel) return;
    if (isMuted) {
      await channel.unmute();
    } else {
      await channel.mute();
    }
  }, [channel, isMuted]);

  const togglePinChannel = useCallback(async () => {
    if (!channel) return;
    if (isChannelPinned) {
      await channel.unpin();
    } else {
      await channel.pin();
    }
  }, [channel, isChannelPinned]);

  useEffect(() => {
    if (!channel) {
      return () => {};
    }

    const handleMessageUpdated = async (event: Event) => {
      const { message } = event;
      if (message && STREAM_MESSAGE_FIELD_PINNED in message) {
        await fetchPinnedMessages();
      }
    };

    channel.on(StreamEventType.MESSAGE_UPDATED, handleMessageUpdated);

    return () => {
      channel.off(StreamEventType.MESSAGE_UPDATED, handleMessageUpdated);
    };
  }, [channel, fetchPinnedMessages]);

  return {
    isChannelPinned,
    isMuted,
    pinnedMessages,
    isPinnedPanelOpen,
    setIsPinnedPanelOpen,
    loadingPinnedMessages,
    isDetailsPanelOpen,
    setIsDetailsPanelOpen,
    toggleMuteChannel,
    togglePinChannel,
    fetchPinnedMessages,
  };
};
